import React, { useEffect, useMemo, useState } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router'
import classNames from 'classnames'

// evertel
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { SessionService } from '@evertel/session/SessionService'
import { getRole } from '@evertel/utils'
import {
    ToggleSwitch, InfoBox, Badge, Button, Card, CardBody,
    Row, Col, Spinner, useUI,
    DataList, DataPair, DataTerm, DataDescription,
    Input
} from '@evertel/web/ui'
import { DepartmentAvatar } from '../view/DepartmentAvatar'
import { CurrentUserController, CurrentUserDepartmentsController, UserController } from '@evertel/blue-user'
import { APIDataDepartmentUserThrough } from '@evertel/types'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { DepartmentController, DepartmentUserController } from '@evertel/department'

interface DeptCardProps {
    userId: number
    departmentId: number
    onLeaveDepartment?: (departmentAccess: APIDataDepartmentUserThrough) => void
}

const DeptCard: React.FC<DeptCardProps> = observer(({
    userId,
    departmentId,
    onLeaveDepartment
}) => {

    const analytics = useService(Analytics)
    const sessionService = useService(SessionService, [])
    const userController = useService(UserController, [userId])
    const departmentController = useService(DepartmentController, [departmentId])
    const currentUserController = useService(CurrentUserController, [])
    const departmentUserController = useService(DepartmentUserController, [])
    const departmentsAccessController = useService(DepartmentsAccessController, [userId])
    const currentUserDepartmentsController = useService(CurrentUserDepartmentsController, [])

    const navigate = useNavigate()
    const { addToast, isConfirmed } = useUI()

    // State
    const [editPositionView, setEditPositionView] = useState(false)
    const [editPositionValue, setEditPositionValue] = useState('')
    const [isLoadingRole, setIsLoadingRole] = useState(false)
    const [isLoadingVerified, setIsLoadingVerified] = useState(false)
    const [isLoadingDetails, setIsLoadingDetails] = useState(false)

    useEffect(() => {
        if (userId && userController.userId !== userId) {
            userController.init(userId)
            departmentsAccessController.init(userId)
        }

        if (departmentId && departmentController.departmentId !== departmentId) {
            departmentController.init(departmentId)
        }
    }, [userId, departmentId])

    // Constants
    const roleOptions = useMemo(() => [
        { role: 'executive', label: 'Executive', description: 'Executives have total access to edit and manage all aspects of your agency account.' },
        { role: 'management', label: 'Management', description: 'Management can manage rooms and users. They cannot run audits, or assign the Executive role.' },
        { role: 'employee', label: 'Employee', description: 'Employees can manage their own account information. They only have access to the rooms they are members of.' },
        { role: 'guest', label: 'Guest', description: 'Guests are non-employees that are invited to specific rooms. They cannot see anything but the contents of the rooms they are in.' }
    ], [])

    const departmentAccess = departmentsAccessController.departmentAccess(departmentId)

    // Consolidated permission checks
    const isMe = sessionService.currentUserId === userId
    const isAdmin = currentUserController.isAdmin

    const isExec = currentUserController.canExecutiveInDepartment(departmentId) || isAdmin

    // Can the current user manage in THIS card's department? Used for action permissions
    // (toggling roles, verified status, etc.) — scoped to the specific department.
    const canManageInDepartment = currentUserController.canManageInDepartment(departmentId)

    // Can the current user manage in their SELECTED department? Used for the visibility
    // guard — a manager/exec viewing another user's profile should see all their department
    // cards, even departments the current user doesn't belong to.
    const canManageInSelectedDepartment = currentUserController.canManage
    const isSelectedDepartment = sessionService.selectedDepartmentId === departmentAccess?.departmentId
    const canToggleAccess = canManageInDepartment || isAdmin
    const canManageDepartment = canToggleAccess || isMe
    const role = getRole(departmentAccess?.roles)

    const deptType = departmentController.department.type

    // Helper function for warning message text
    const getWarningMessage = () => {
        if (isMe) {
            return 'Pending management verification in this agency'
        }
        return (
            <span>This user can login to the Agency but <strong>does not have access</strong> to any rooms or documents</span>
        )
    }

    // Event Handlers
    const gotoDept = () => {
        navigate('/admin/agency/' + departmentId)
    }

    const toggleVerified = async () => {
        const newIsVerified = !departmentAccess.isVerified

        // Guards for revoking access — always confirm before removing
        if (!newIsVerified) {
            if (isMe && !isAdmin) {
                // Non-admin revoking their own access: they'll lose access and can't undo it
                const confirmed = await isConfirmed({
                    message: `You're about to revoke your own access to this agency. You will be removed from all rooms and will no longer have access. You won't be able to undo this without help from a manager or executive. Are you sure?`,
                    acceptButton: { label: 'Yes, revoke my access', color: 'danger' }
                })
                if (!confirmed) return
            } else {
                // Admin or manager revoking someone else's (or admin revoking own) access
                const confirmed = await isConfirmed({
                    message: `Revoking this user's access will remove them from all rooms in this agency. Are you sure?`,
                    acceptButton: { label: 'Yes, revoke access', color: 'danger' }
                })
                if (!confirmed) return
            }
        }

        setIsLoadingVerified(true)

        try {
            await departmentsAccessController.setVerified(departmentAccess.departmentId, newIsVerified)

            const verifiedText = newIsVerified
                ? 'This user\'s access is now GRANTED'
                : 'This user\'s access has been REMOVED and will no longer have access to your agency in Evertel'
            const color = newIsVerified ? 'success' : 'danger'
            addToast({
                color: color,
                message: verifiedText
            })
        } catch (e) {
            addToast({
                color: 'danger',
                message: e.message || e
            })
        }

        setIsLoadingVerified(false)

    }

    const roleRank: Record<string, number> = { executive: 3, management: 2, employee: 1, guest: 0 }

    const toggleRole = async (checked: boolean, newRole: string) => {
        if (!isExec && newRole === 'executive') return

        //prevent clicking currently selected role, can instead click the intended role
        if (checked || isLoadingRole) return

        // Warn if a non-admin is about to demote themselves
        if (isMe && !isAdmin && (roleRank[newRole] ?? 0) < (roleRank[role] ?? 0)) {
            const confirmed = await isConfirmed({
                message: `You're about to change your own role to ${newRole}. You won't be able to change it back without help from another manager or executive. Are you sure?`,
                acceptButton: { label: 'Yes, change my role', color: 'danger' }
            })
            if (!confirmed) return
        }

        setIsLoadingRole(true)

        try {
            await departmentsAccessController.setRole(departmentAccess.departmentId, newRole)

            analytics.logEvent('User', 'edit_user_role')
        } catch (e) {
            addToast({
                color: 'danger',
                message: e.message || e
            })
        }

        setIsLoadingRole(false)

    }

    const removeFromDepartment = async () => {
        try {
            await departmentsAccessController.removeDepartmentAccess(departmentAccess.departmentId)

            analytics.logEvent('User', 'leave_department')

            const message = isMe
                ? `You have successfully left ${departmentController.shortName}`
                : `User has been successfully removed from ${departmentController.shortName}`

            addToast({
                color: 'success',
                message: message
            })

            if (onLeaveDepartment) onLeaveDepartment(departmentAccess)
        } catch (e) {
            addToast({
                color: 'danger',
                message: e.message || e
            })
        }

    }

    const selectDepartment = (deptId: number) => {
        // set the new department as selected
        sessionService.setSelectedDeparmentId(deptId)
    }

    // Consolidated function for updating department details
    const updateDepartmentDetails = async (updates: { isPrimary?: boolean, position?: string }) => {

        if (isLoadingDetails) return

        if (updates.isPrimary !== undefined) {
            if (!isMe) return

            if (updates.isPrimary && (!departmentAccess.positionString || !departmentAccess.isVerified)) {
                addToast({
                    title: 'Primary Agency',
                    color: 'danger',
                    message: 'Unable to set agency as primary without position or verification.'
                })
                return
            }
        }

        setIsLoadingDetails(true)

        try {
            await departmentUserController.updateDetails(departmentId, userId, {
                isPrimary: updates.isPrimary !== undefined ? updates.isPrimary : departmentAccess.isPrimary,
                position: updates.position !== undefined ? updates.position : departmentAccess.positionString
            })

            // Handle primary department changes
            if (updates.isPrimary !== undefined) {
                const previousPrimaryDepartment = currentUserDepartmentsController.departmentsAccess
                    .find(da => da.isPrimary && da.departmentId !== departmentId)

                if (updates.isPrimary && previousPrimaryDepartment) {
                    // after API call this would have been removed so need local data to match remote
                    runInAction(() => {
                        previousPrimaryDepartment.isPrimary = false
                    })
                }

                const message = updates.isPrimary
                    ? `Successfully changed to ${departmentController.shortName}.`
                    : `${departmentController.shortName} is no longer your primary agency.`

                addToast({
                    title: 'Primary Agency',
                    color: 'success',
                    message
                })
            }

            // Handle position updates
            if (updates.position !== undefined) {
                addToast({
                    color: 'success',
                    message: 'Position updated successfully'
                })
                setEditPositionView(false)
            }
        } catch (e: any) {
            const title = updates.isPrimary !== undefined ? 'Primary Agency' : 'Position'
            addToast({
                title,
                color: 'danger',
                message: e.message || 'Failed to update'
            })
        } finally {
            setIsLoadingDetails(false)
        }
    }

    const togglePrimary = async () => {
        const newPrimaryValue = !departmentAccess.isPrimary
        await updateDepartmentDetails({ isPrimary: newPrimaryValue })
    }

    const handleEditPosition = () => {
        setEditPositionValue(departmentAccess.positionString || '')
        setEditPositionView(true)
    }

    const handleCancelEditPosition = () => {
        setEditPositionView(false)
        setEditPositionValue('')
    }

    const handleSavePosition = async () => {
        if (editPositionValue === departmentAccess.positionString) {
            setEditPositionView(false)
            return
        }

        await updateDepartmentDetails({ position: editPositionValue })
    }

    //==================================
    // INTERNAL COMPONENTS
    //==================================
    const renderAgencyName = () => (
        <DataPair indent={0} className="align-items-stretch">
            <DataTerm>
                <div className="d-flex align-items-center h-100">
                    Agency:
                </div>
            </DataTerm>
            <DataDescription>
                <Row align="start" valign="center">
                    <DepartmentAvatar
                        departmentId={departmentAccess.departmentId}
                        width={45}
                        height={45}
                        className="me-3"
                        userId={userId}
                    />
                    <div className="ms-2 ">
                        <h4 className="weight-900 mb-0">
                            {departmentController.department.name}
                        </h4>
                        {isAdmin && (
                            <Button
                                color="link"
                                size="sm"
                                className="mt-0 pt-0 align-self-start"
                                onClick={gotoDept}>
                                View Agency
                            </Button>
                        )}
                    </div>
                </Row>
            </DataDescription>
        </DataPair>
    )

    const renderIsPrimary = () => {
        const isPrimary = departmentAccess.isPrimary
        const userNotVerified = !departmentAccess.isVerified

        return (
            <DataPair>
                <DataTerm>My Primary Agency:</DataTerm>
                <DataDescription>
                    <Row valign="center">
                        {isMe && (
                            <>
                                <ToggleSwitch
                                    id={'toggle-primary-' + departmentAccess.departmentId}
                                    onClick={togglePrimary}
                                    checked={isPrimary}
                                    className="me-3"
                                    color="primary"
                                    optionLabels={['\u2713', '\u2715']}
                                    disabled={isLoadingDetails || userNotVerified || !departmentAccess.positionString}
                                />
                                {userNotVerified ? (
                                    <small>You must have verified access to this agency before making it your primary</small>
                                ) : !departmentAccess.positionString ? (
                                    <small>You must set a position before making this your primary agency</small>
                                ) : null}
                            </>
                        )}
                        {isPrimary &&
                            <Badge
                                color="success"
                                className="me-2"
                            >
                                Primary Agency
                            </Badge>
                        }
                    </Row>
                </DataDescription>
            </DataPair>
        )
    }

    const renderPosition = () => {
        return (
            <DataPair>
                <DataTerm>Position:</DataTerm>
                <DataDescription>
                    {editPositionView ? (
                        <Row valign="center">
                            <Input
                                name="editPosition"
                                value={editPositionValue}
                                onChange={(e) => setEditPositionValue(e.target.value)}
                                disabled={isLoadingDetails}
                                style={{ maxWidth: 300 }}
                            />
                            <Button
                                color="primary"
                                size="sm"
                                className="ms-2"
                                onClick={handleSavePosition}
                                disabled={isLoadingDetails}
                            >
                                {isLoadingDetails ? 'Saving...' : 'Save Position'}
                            </Button>
                            <Button
                                color="link"
                                size="sm"
                                className="ms-2"
                                onClick={handleCancelEditPosition}
                                disabled={isLoadingDetails}
                            >
                                Cancel
                            </Button>
                        </Row>
                    ) : (
                        <Row>
                            {departmentAccess.positionString || <em>Not Set</em>}
                            {canManageDepartment &&
                            <Button
                                color="link"
                                size="sm"
                                className="ms-2"
                                onClick={handleEditPosition}>
                                edit
                            </Button>
                            }
                        </Row>
                    )}
                </DataDescription>
            </DataPair>
        )
    }

    const renderVerifiedSwitch = () => {
        const getAccessBadgeText = () => {
            if (departmentAccess.isVerified) return 'Granted'
            return isMe ? 'Pending' : 'No Access'
        }

        return (
            <DataPair>
                <DataTerm>Access:</DataTerm>
                <DataDescription>
                    <Row valign="center">
                        {canToggleAccess && (
                            <ToggleSwitch
                                id={'toggle-verify-' + departmentAccess.departmentId}
                                onClick={toggleVerified}
                                checked={departmentAccess.isVerified}
                                className={'me-3 float-left'}
                                color={'success'}
                                optionLabels={['\u2713', '\u2715']}
                                disabled={isLoadingVerified}
                            />
                        )}
                        {isLoadingVerified ? (
                            <Spinner width={25} />
                        ) : (
                            <Badge
                                color={departmentAccess.isVerified ? 'success' : 'warning'}
                                className="me-2"
                                style={{ maxWidth: '80px', textAlign: 'center' }}
                            >
                                {getAccessBadgeText()}
                            </Badge>
                        )}
                    </Row>
                </DataDescription>
            </DataPair>
        )
    }

    const renderRoles = () => {
        if (!canToggleAccess) {
            return (
                <DataPair className={classNames('animated', { hide: !departmentAccess.isVerified })}>
                    <DataTerm>Role:</DataTerm>
                    <DataDescription>
                        <span className="text-capitalize">{role}</span>
                    </DataDescription>
                </DataPair>
            )
        }

        return (
            <DataPair leftWidth={'15%'} indent={10} className={classNames('animated', { hide: !departmentAccess.isVerified })}>
                <DataTerm>
                    Role: {isLoadingRole && <Spinner width={25} />}
                </DataTerm>
                <DataDescription>
                    {roleOptions?.map((option, idx) => {
                        const htmlId=`${idx}-${departmentAccess.departmentId}-${userId}`
                        return (
                            <Row key={idx}>
                                <Col className="flex-grow-0" valign="start">
                                    <ToggleSwitch
                                        id={htmlId}
                                        onClick={(e) => toggleRole(role === option.role, option.role)}
                                        checked={role === option.role}
                                        className={'me-3 float-left'}
                                        color="primary"
                                        // only execs and admins can assign the 'executive' role
                                        disabled={(!isExec && option.role === 'executive') || isLoadingRole || (!departmentAccess.isVerified)}
                                        optionLabels={['\u2713', '\u2715']}
                                    />
                                </Col>
                                <Col>
                                    <div>
                                        <label className="text-capitalize cursor-pointer" htmlFor={htmlId}><strong>{option.role}</strong></label>
                                    </div>
                                    <small>{option.description}</small>
                                </Col>
                            </Row>
                        )})}
                </DataDescription>
            </DataPair>
        )
    }

    const renderAgencyLinks = () => {
        // Only show controls for admins, the user themselves, or managers of this department
        if (!canManageDepartment) return null

        // Determine button text based on user role and status
        const getButtonText = () => {
            if (!isMe || isAdmin) {
                return `Remove ${userController.fullName} from this agency`
            }
            return departmentAccess.isVerified ? 'Leave this agency' : 'Cancel Request to Join'
        }

        const canSwitch = isMe && !isSelectedDepartment && departmentAccess.isVerified

        return (
            <Row className="p-2 pb-0" align="between">
                <Button
                    color="link"
                    onClick={async () => {
                        const result = await isConfirmed({
                            message: isMe ? 'Are you sure you want to leave this agency?' : `Are you sure you want to remove this user from ${departmentController.shortName}?`,
                            acceptButton: { label: 'Yes, remove', color: 'danger' }
                        })
                        if (result) {
                            removeFromDepartment()
                        }
                    }}>
                    {getButtonText()}
                </Button>
                {canSwitch &&
                <Button
                    color="link"
                    onClick={() => selectDepartment(departmentAccess.departmentId)}
                >
                    Switch to Agency
                </Button>
                }
            </Row>
        )
    }

    // Visibility guard: you can see this card if it's your own profile, you're an admin,
    // or you're a manager/exec in your selected department. This intentionally checks the
    // selected department (not this card's department) because a manager viewing another
    // user's profile needs to see all their department memberships, including departments
    // the current user isn't a member of.
    if (!isAdmin && !isMe && !canManageInSelectedDepartment) {
        return <InfoBox color="danger" className="mt-3">Sorry, you do not have the appropriate permissions to view this information</InfoBox>
    }

    if (!departmentController.department.id || !departmentAccess) {
        return <InfoBox color="info">Loading department information...</InfoBox>
    }

    return (
        <Card className={classNames('agency-card', 'mt-3', 'border', { 'border-primary': departmentAccess.isVerified })}>
            <CardBody>
                <DataList
                    leftWidth={'20%'}
                    breakAt={650}
                    indent={35}
                >
                    {renderAgencyName()}
                    {!departmentAccess.isVerified && (
                        <DataPair leftWidth="0%">
                            <DataDescription>
                                <InfoBox color="warning" className="mt-2">
                                    {getWarningMessage()}
                                </InfoBox>
                            </DataDescription>
                        </DataPair>
                    )}
                    {isMe && (
                        <>
                            {renderIsPrimary()}
                        </>
                    )}
                    {renderPosition()}
                    {renderVerifiedSwitch()}
                    {renderRoles()}
                    {renderAgencyLinks()}
                </DataList>
            </CardBody>
        </Card>
    )
})

export { DeptCard }
